import type { BECurrentMatchdayWindow } from '@/lib/network';

/**
 * The settling period that starts when a gameweek locks, taken from
 * `/api/teams/current-matchday-window`.
 *
 * `currentGameweek` is the gameweek being played or just finished, so once its
 * deadline (`locksAt`) has passed, points and team state are being settled and
 * nothing the user changes now lands in it. There is no end time: the pop-up is
 * closed by the `enable_team_transition_popup` app setting, which an admin
 * turns off once the transition is done.
 */
export interface TeamUpdatingWindow {
  /** Exclusive start - the current gameweek's deadline. */
  from: number;
  /** Gameweek being settled, for the "GW4 is complete" copy. */
  gameweekNumber: number | null;
  /**
   * Identifies the window, so a dismissal does not carry into the next one.
   * Falls back to the start time for the rare gameweek row with no id.
   */
  key: string;
}

const toTimestamp = (value: string | null | undefined): number | null => {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
};

/**
 * Reads the settling period out of a matchday-window response, or `null` when
 * there is no gameweek deadline to measure from: the knockout axis (no
 * gameweeks at all) and a gameweek with no deadline recorded.
 */
export const getTeamUpdatingWindow = (
  matchdayWindow?: BECurrentMatchdayWindow | null
): TeamUpdatingWindow | null => {
  const current = matchdayWindow?.currentGameweek;
  const from = toTimestamp(current?.locksAt);

  if (from === null) {
    return null;
  }

  // The copy names the gameweek that has COMPLETED, not the one locking in.
  // When the current gameweek is still being played (locked/live) or open, the
  // gameweek that just completed is the one before it; only when the current
  // gameweek is itself finalised is it the completed one. e.g. when GW5 locks
  // in, GW4 is the gameweek that has completed.
  const currentNumber = current?.number ?? null;
  const completedNumber =
    currentNumber === null
      ? null
      : current?.status === 'finalised'
        ? currentNumber
        : currentNumber - 1;

  return {
    from,
    // Below the first real gameweek (e.g. the very first lock, which has no
    // completed predecessor) the copy falls back to the generic "The gameweek".
    gameweekNumber: completedNumber !== null && completedNumber >= 1 ? completedNumber : null,
    // `|| ` not `??`: a blank id is as unusable as a missing one.
    key: current?.id?.trim() || String(from),
  };
};

export const isWithinTeamUpdatingWindow = (
  updatingWindow: TeamUpdatingWindow | null,
  now: number
): boolean => !!updatingWindow && now > updatingWindow.from;
